// ======================================================================
// FILE: src/modules/wms/layout/WMSSidebar.jsx
// MODULE: Work Management System (WMS)
// PURPOSE:
//   Sidebar navigation for WMS module
//   - RBAC driven
//   - ERP standard layout
//   - Collapsible sidebar
// ======================================================================

import React, {
  useContext,
  useEffect,
  useState,
} from "react";

import {
  NavLink,
  useLocation,
  Link,
} from "react-router-dom";

import { AuthContext }
from "../../../context/AuthContext";

// ----------------------------------------------------------------------
// PERMISSION HELPER
// ----------------------------------------------------------------------

const canAny = (
  permissions,
  keys = []
) =>
  keys.some(
    (k) => permissions?.[k]?.view === true
  );

// ----------------------------------------------------------------------
// MENU
// ----------------------------------------------------------------------

const MENU = [

  // ==========================================================
  // DASHBOARD
  // ==========================================================
  {
    label: "Dashboard",
    route: "/wms/dashboard",
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-chart-line",
  },

  // ==========================================================
  // OPERATIONS
  // ==========================================================
  {
    type: "group",
    label: "Operations",
  },

  {
    label: "My Work",
    route: "/wms/my-work",
    perms: ["WMS_MY_WORK"],
    icon: "fas fa-user-check",
  },
  {
    label: "Executor Workspace",
    route: "/wms/executor-workspace",
    perms: ["WMS_MY_WORK"],
    icon: "fas fa-laptop-house",
},
{
    label: "Approval Workspace",
    route: "/wms/approval-workspace",
    perms: ["WMS_WORK"],
    icon: "fas fa-user-check",
},
  {
    label: "Works",
    route: "/wms/works",
    perms: ["WMS_WORK"],
    icon: "fas fa-briefcase"
  },
  {
    label: "Recurring Schedules",
    route: "/wms/recurring-schedules",
    perms: ["WMS_WORK"],
    icon: "fas fa-sync-alt",
},
  {
    label: "Templates",
    route: "/wms/templates",
    perms: ["WMS_TEMPLATE"],
    icon: "fas fa-copy",
  },

  // ==========================================================
  // QUALITY & COMPLIANCE
  // ==========================================================
  {
    type: "group",
    label: "Quality & Compliance",
  },

  {
    label: "Checklists",
    route: "/wms/checklists",
    perms: ["WMS_CHECKLIST"],
    icon: "fas fa-clipboard-check",
  },

  // ==========================================================
  // ORGANIZATION
  // ==========================================================
  {
    type: "group",
    label: "Organization",
  },

  {
    label: "Teams",
    route: "/wms/teams",
    perms: ["WMS_TEAM"],
    icon: "fas fa-users",
  },

  // ==========================================================
  // CONFIGURATION
  // ==========================================================
  {
    type: "group",
    label: "Configuration",
  },

  {
    label: "Calendar",
    route: "/wms/calendars",
    perms: ["WMS_CALENDAR"],
    icon: "fas fa-calendar-alt",
  },

  {
    label: "SLA Management",
    route: "/wms/sla-policies",
    perms: ["WMS_SLA"],
    icon: "fas fa-clock",
  },

  {
    label: "Escalation",
    route: "/wms/escalation-policies",
    perms: ["WMS_ESCALATION"],
    icon: "fas fa-level-up-alt",
  },

  // ==========================================================
  // REPORTS
  // ==========================================================
  {
    type: "group",
    label: "Reports",
  },

  {
    label: "Open Work Register",
      route: "/wms/reports/open-work-register",
      perms: ["WMS_MY_WORK"],
      icon: "fas fa-chart-bar",
  },
];

// ======================================================================
// COMPONENT
// ======================================================================

export default function WMSSidebar({
  onToggle,
}) {

  const { permissions } =
    useContext(AuthContext);

  const location =
    useLocation();

  const collapsedLS =
    localStorage.getItem(
      "wms_sidebar_collapsed"
    );

  const [expanded, setExpanded] =
    useState(
      collapsedLS === null
        ? true
        : collapsedLS === "false"
    );

  useEffect(() => {

    localStorage.setItem(
      "wms_sidebar_collapsed",
      expanded ? "false" : "true"
    );

    onToggle?.(expanded);

  }, [expanded, onToggle]);

  return (

    <aside
      style={{
        width: expanded ? 260 : 64,
        transition: "width 0.22s ease",
        background: "#111827",
        color: "#fff",
        minHeight: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 100,
      }}
    >

      {/* HEADER */}
      <div
        style={{
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: expanded
            ? "space-between"
            : "center",
          padding: expanded
            ? "0 12px"
            : "0",
          borderBottom:
            "1px solid #374151",
        }}
      >

        {expanded && (
          <span
            style={{
              fontWeight: 700,
            }}
          >
            WMS
          </span>
        )}

        <button
          onClick={() =>
            setExpanded((v) => !v)
          }
          style={{
            background: "none",
            border: "none",
            color: "#9ca3af",
          }}
        >
          <i className="fas fa-bars" />
        </button>

      </div>

      {/* BACK TO DASHBOARD */}

      <Link
        to="/dashboard"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          margin: 12,
          padding: "10px 12px",
          borderRadius: 999,
          background: "#2563eb",
          color: "#fff",
          textDecoration: "none",
          justifyContent: expanded
            ? "flex-start"
            : "center",
        }}
      >
        <i className="fas fa-home" />

        {expanded && (
          <span>
            Back to Dashboard
          </span>
        )}
      </Link>

      {/* MENU */}

      <nav
        style={{
          maxHeight:
            "calc(100vh - 140px)",
          overflowY: "auto",
        }}
      >

        {MENU.map((item, idx) => {

          if (
            item.type === "group"
          ) {

            if (!expanded)
              return null;

            return (
              <div
                key={`grp-${idx}`}
                style={{
                  margin:
                    "12px 14px 6px",
                  fontSize: 11,
                  color: "#9ca3af",
                  textTransform:
                    "uppercase",
                }}
              >
                {item.label}
              </div>
            );
          }

          if (
            !canAny(
              permissions,
              item.perms
            )
          ) {
            return null;
          }

          const active =
            location.pathname.startsWith(
              item.route
            );

          return (

            <NavLink
              key={item.route}
              to={item.route}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: expanded
                  ? "10px 14px"
                  : "10px 0",
                justifyContent:
                  expanded
                    ? "flex-start"
                    : "center",
                color: active
                  ? "#fff"
                  : "#cbd5e1",
                background: active
                  ? "#374151"
                  : "transparent",
                textDecoration:
                  "none",
                fontSize: 14,
              }}
            >
              <i
                className={
                  item.icon
                }
              />

              {expanded && (
                <span>
                  {item.label}
                </span>
              )}

            </NavLink>
          );
        })}

      </nav>

    </aside>
  );
}